/**
 * TechnicianProjectDetail Page
 *
 * Detailed view of a specific project for technicians.
 * Shows project info, assigned tasks, team members, progress photos, and chat access.
 * Excludes financial data (budget, quotations, expenses) that are admin-only.
 *
 * @module pages/technician/TechnicianProjectDetail
 */

import React, { useState, useMemo } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Tab,
  Tabs,
  Spinner,
  Alert,
  ProgressBar,
} from 'react-bootstrap';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { toast } from '../../utils/toast';
import {
  getProjectDetail,
  getProjectTeam,
  getProjectPhotos,
} from '../../services/technicianProjectService';
import { PROJECT_STATUS, CACHE_TIME } from '../../utils/constants';
import TaskStatusBadge from '../../components/tasks/shared/TaskStatusBadge';
import TaskPriorityBadge from '../../components/tasks/shared/TaskPriorityBadge';
import TaskAssigneeAvatar from '../../components/tasks/shared/TaskAssigneeAvatar';
import ProjectTimeline from '../../components/timeline/ProjectTimeline';
import './TechnicianPages.css';

/**
 * Status badge configuration
 */
const STATUS_CONFIG = {
  [PROJECT_STATUS.QUOTATION_SENT]: { variant: 'warning', label: 'Cotización Enviada', icon: 'bi-file-text' },
  [PROJECT_STATUS.QUOTATION_ACCEPTED]: { variant: 'info', label: 'Cotización Aceptada', icon: 'bi-file-check' },
  [PROJECT_STATUS.PO_RECEIVED]: { variant: 'primary', label: 'OC Recibida', icon: 'bi-receipt' },
  [PROJECT_STATUS.IN_PROGRESS]: { variant: 'success', label: 'Activo', icon: 'bi-play-circle' },
  [PROJECT_STATUS.CLOSED]: { variant: 'dark', label: 'Cerrado', icon: 'bi-lock' },
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  // Date-only strings (e.g. "2026-07-07") are parsed as UTC midnight by `new Date()`;
  // formatting them in the local timezone can shift the displayed day. Force UTC for those.
  const isDateOnly = typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  return new Date(dateString).toLocaleDateString('es-CR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(isDateOnly && { timeZone: 'UTC' }),
  });
};

/**
 * Role label mapping
 */
const ROLE_LABELS = {
  admin: 'Administrador',
  supervisor: 'Supervisor',
  technician: 'Tecnico',
  employee: 'Tecnico',
  operario: 'Operario',
  mensajero: 'Mensajero',
  client: 'Cliente',
};

const TechnicianProjectDetail = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { conversations } = useChat();
  const [searchParams] = useSearchParams();
  // `?tab=photos` lets other screens (e.g. ProgressUpload after a successful
  // upload) land the technician on the tab that shows what they just did.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  // The "Chat" button used to navigate to `/tecnico/mensajes/${projectId}`, but
  // that route is `mensajes/:conversationId`: the project id was fed straight to
  // `getConversation()`, which `.single()`s on `chat_conversations.id` → PGRST116
  // → blank chat with an empty header, and any message sent from it blew up on
  // the FK. The id we need is the conversation's, and the only conversations the
  // technician can actually open are the ones they participate in (RLS:
  // `is_chat_participant(id, auth.uid())` — a technician is NOT covered by the
  // `type='project' AND role IN (admin, employee)` branch). So: resolve it from
  // the conversations they already have, and if there is none, say so instead of
  // routing them into a broken screen.
  const projectConversation = useMemo(
    () => conversations?.find((c) => c.project_id === projectId) || null,
    [conversations, projectId]
  );

  const handleOpenChat = () => {
    if (projectConversation) {
      navigate(`/tecnico/mensajes/${projectConversation.id}`);
      return;
    }
    toast.info('Este proyecto todavia no tiene un chat en el que participes');
    navigate('/tecnico/mensajes');
  };

  // Fetch project detail
  const {
    data: projectResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['technician-project-detail', projectId],
    queryFn: () => getProjectDetail(projectId),
    enabled: !!projectId,
    staleTime: CACHE_TIME.MEDIUM,
  });

  // Fetch team members (lazy - only when tab is active)
  const { data: teamResponse, isLoading: teamLoading } = useQuery({
    queryKey: ['technician-project-team', projectId],
    queryFn: () => getProjectTeam(projectId),
    enabled: !!projectId && activeTab === 'team',
    staleTime: CACHE_TIME.LONG,
  });

  // Fetch photos (lazy)
  const { data: photosResponse, isLoading: photosLoading } = useQuery({
    queryKey: ['technician-project-photos', projectId],
    queryFn: () => getProjectPhotos(projectId),
    enabled: !!projectId && activeTab === 'photos',
    staleTime: CACHE_TIME.MEDIUM,
  });

  const project = projectResponse?.data;
  const tasks = project?.tasks || [];
  const taskSummary = useMemo(() => project?.taskSummary || {}, [project?.taskSummary]);
  const team = teamResponse?.data || [];
  const photos = photosResponse?.data || [];

  // Task progress percentage
  const progressPercent = useMemo(() => {
    if (!taskSummary.total) return 0;
    return Math.round((taskSummary.completed / taskSummary.total) * 100);
  }, [taskSummary]);

  const statusConfig = STATUS_CONFIG[project?.status] || STATUS_CONFIG[PROJECT_STATUS.QUOTATION_SENT];

  // Loading state
  if (isLoading) {
    return (
      <Container fluid className="technician-page p-0">
        <div className="page-header px-3 pt-3 pb-2">
          <Link to="/tecnico/proyectos" className="text-decoration-none text-muted d-inline-flex align-items-center mb-2">
            <i className="bi bi-arrow-left me-1"></i> Volver
          </Link>
          <h1 className="page-title">Cargando...</h1>
        </div>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando proyecto...</p>
        </div>
      </Container>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <Container fluid className="technician-page p-0">
        <div className="page-header px-3 pt-3 pb-2">
          <Link to="/tecnico/proyectos" className="text-decoration-none text-muted d-inline-flex align-items-center mb-2">
            <i className="bi bi-arrow-left me-1"></i> Volver
          </Link>
          <h1 className="page-title">Error</h1>
        </div>
        <div className="px-3">
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error?.message || 'No se pudo cargar el proyecto'}
          </Alert>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="technician-page p-0">
      {/* Header */}
      <div className="page-header px-3 pt-3 pb-2">
        <Link to="/tecnico/proyectos" className="text-decoration-none text-muted d-inline-flex align-items-center mb-2">
          <i className="bi bi-arrow-left me-1"></i> Volver
        </Link>
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>
            {project.project_number}
          </span>
          <Badge bg={statusConfig.variant}>
            <i className={`bi ${statusConfig.icon} me-1`}></i>
            {statusConfig.label}
          </Badge>
        </div>
        <h1 className="page-title">{project.title}</h1>
        {project.client && (
          <p className="page-subtitle mb-0">
            <i className="bi bi-building me-1"></i>
            {project.client.company_name || project.client.primary_contact_name}
          </p>
        )}
      </div>

      {/* Quick Stats */}
      <div className="px-3 mb-3">
        <Card className="dashboard-card">
          <Card.Body className="py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">Progreso de Tareas</small>
              <small className="fw-bold">{progressPercent}%</small>
            </div>
            <ProgressBar
              now={progressPercent}
              variant={progressPercent === 100 ? 'success' : progressPercent > 50 ? 'primary' : 'warning'}
              style={{ height: '8px' }}
            />
            <Row className="g-2 mt-2 text-center">
              <Col xs={3}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ffc107' }}>
                  {taskSummary.pending || 0}
                </div>
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Pendientes</small>
              </Col>
              <Col xs={3}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0d6efd' }}>
                  {taskSummary.inProgress || 0}
                </div>
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>En Progreso</small>
              </Col>
              <Col xs={3}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#198754' }}>
                  {taskSummary.completed || 0}
                </div>
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Completadas</small>
              </Col>
              <Col xs={3}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#dc3545' }}>
                  {taskSummary.blocked || 0}
                </div>
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Bloqueadas</small>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="px-3 mb-3 d-flex gap-2">
        <button
          className="btn btn-outline-primary btn-sm flex-fill"
          onClick={() => setActiveTab('bitacora')}
        >
          <i className="bi bi-clock-history me-1"></i>Bitacora
        </button>
        <button
          className="btn btn-outline-primary btn-sm flex-fill"
          onClick={handleOpenChat}
        >
          <i className="bi bi-chat-dots me-1"></i>Chat
        </button>
        <button
          className="btn btn-outline-primary btn-sm flex-fill"
          onClick={() => setActiveTab('tasks')}
        >
          <i className="bi bi-list-check me-1"></i>Tareas
        </button>
        <button
          className="btn btn-outline-primary btn-sm flex-fill"
          onClick={() => setActiveTab('photos')}
        >
          <i className="bi bi-images me-1"></i>Fotos
        </button>
      </div>

      {/* Tabs */}
      <div className="px-3 pb-3">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
          fill
        >
          {/* Overview Tab */}
          <Tab eventKey="overview" title={<><i className="bi bi-info-circle me-1"></i><span className="d-none d-sm-inline">Resumen</span></>}>
            <Card className="dashboard-card mb-3">
              <Card.Body>
                <h6 className="mb-3" style={{ fontWeight: 600 }}>
                  <i className="bi bi-file-text me-2 text-primary"></i>
                  Informacion del Proyecto
                </h6>

                {project.description && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Descripcion</small>
                    <p className="mb-0" style={{ fontSize: '0.9rem' }}>{project.description}</p>
                  </div>
                )}

                <Row className="g-3">
                  <Col xs={6}>
                    <small className="text-muted d-block">Fecha de Creacion</small>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {formatDate(project.created_at)}
                    </span>
                  </Col>
                  {project.estimated_payment_date && (
                    <Col xs={6}>
                      <small className="text-muted d-block">Pago Estimado</small>
                      <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {formatDate(project.estimated_payment_date)}
                      </span>
                    </Col>
                  )}
                  {project.po_number && (
                    <Col xs={6}>
                      <small className="text-muted d-block">Orden de Compra</small>
                      <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {project.po_number}
                      </span>
                    </Col>
                  )}
                  {project.created_by_profile && (
                    <Col xs={6}>
                      <small className="text-muted d-block">Gerente de Proyecto</small>
                      <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {project.created_by_profile.full_name}
                      </span>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            {/* Client Info */}
            {project.client && (
              <Card className="dashboard-card mb-3">
                <Card.Body>
                  <h6 className="mb-3" style={{ fontWeight: 600 }}>
                    <i className="bi bi-building me-2 text-primary"></i>
                    Cliente
                  </h6>
                  <div className="mb-2">
                    <strong>{project.client.company_name}</strong>
                  </div>
                  {project.client.primary_contact_name && (
                    <div className="mb-1" style={{ fontSize: '0.9rem' }}>
                      <i className="bi bi-person me-2 text-muted"></i>
                      {project.client.primary_contact_name}
                    </div>
                  )}
                  {project.client.primary_contact_phone && (
                    <div className="mb-1" style={{ fontSize: '0.9rem' }}>
                      <a href={`tel:${project.client.primary_contact_phone}`} className="text-decoration-none">
                        <i className="bi bi-telephone me-2 text-muted"></i>
                        {project.client.primary_contact_phone}
                      </a>
                    </div>
                  )}
                  {project.client.primary_contact_email && (
                    <div style={{ fontSize: '0.9rem' }}>
                      <a href={`mailto:${project.client.primary_contact_email}`} className="text-decoration-none">
                        <i className="bi bi-envelope me-2 text-muted"></i>
                        {project.client.primary_contact_email}
                      </a>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Hours Summary */}
            {(taskSummary.totalEstimatedHours > 0 || taskSummary.totalActualHours > 0) && (
              <Card className="dashboard-card">
                <Card.Body>
                  <h6 className="mb-3" style={{ fontWeight: 600 }}>
                    <i className="bi bi-clock me-2 text-primary"></i>
                    Horas
                  </h6>
                  <Row className="g-3 text-center">
                    <Col xs={6}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0d6efd' }}>
                        {taskSummary.totalEstimatedHours}h
                      </div>
                      <small className="text-muted">Estimadas</small>
                    </Col>
                    <Col xs={6}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#198754' }}>
                        {taskSummary.totalActualHours}h
                      </div>
                      <small className="text-muted">Registradas</small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Tab>

          {/* Tasks Tab */}
          <Tab eventKey="tasks" title={<><i className="bi bi-list-check me-1"></i><span className="d-none d-sm-inline">Tareas</span> <Badge bg="secondary" pill style={{ fontSize: '0.65rem' }}>{taskSummary.total || 0}</Badge></>}>
            {tasks.length > 0 ? (
              tasks.map((task) => (
                <Card
                  key={task.id}
                  className="dashboard-card mb-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tecnico/tareas/${task.id}`)}
                  role="button"
                >
                  <Card.Body className="py-3">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <div className="flex-grow-1 me-2">
                        <small className="text-muted">{task.task_number}</small>
                        <h6 className="mb-1" style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          {task.title}
                        </h6>
                      </div>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </div>
                    <div className="d-flex gap-1 flex-wrap align-items-center">
                      <TaskStatusBadge status={task.status} />
                      <TaskPriorityBadge priority={task.priority} />
                      {task.due_date && (
                        <small className="text-muted ms-auto">
                          <i className="bi bi-calendar me-1"></i>
                          {formatDate(task.due_date)}
                        </small>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ))
            ) : (
              <Card className="dashboard-card">
                <Card.Body className="text-center py-4">
                  <i className="bi bi-list-check display-4 text-muted mb-2 d-block"></i>
                  <p className="text-muted mb-0">No tienes tareas asignadas en este proyecto</p>
                </Card.Body>
              </Card>
            )}
          </Tab>

          {/* Team Tab */}
          <Tab eventKey="team" title={<><i className="bi bi-people me-1"></i><span className="d-none d-sm-inline">Equipo</span></>}>
            {teamLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="mt-2 text-muted small">Cargando equipo...</p>
              </div>
            ) : team.length > 0 ? (
              team.map((member) => (
                <Card key={member.id} className="dashboard-card mb-2">
                  <Card.Body className="py-3 d-flex align-items-center gap-3">
                    <TaskAssigneeAvatar assignee={member} size="lg" />
                    <div className="flex-grow-1">
                      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        {member.full_name}
                        {member.isProjectManager && (
                          <Badge bg="primary" className="ms-2" style={{ fontSize: '0.6rem' }}>PM</Badge>
                        )}
                        {member.id === user?.id && (
                          <Badge bg="outline-secondary" className="ms-1 border" style={{ fontSize: '0.6rem', color: '#6c757d' }}>Tu</Badge>
                        )}
                      </div>
                      <small className="text-muted">
                        {ROLE_LABELS[member.role] || member.role}
                      </small>
                    </div>
                    {member.phone && (
                      <a href={`tel:${member.phone}`} className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-telephone"></i>
                      </a>
                    )}
                  </Card.Body>
                </Card>
              ))
            ) : (
              <Card className="dashboard-card">
                <Card.Body className="text-center py-4">
                  <i className="bi bi-people display-4 text-muted mb-2 d-block"></i>
                  <p className="text-muted mb-0">No se encontraron miembros del equipo</p>
                </Card.Body>
              </Card>
            )}
          </Tab>

          {/* Bitacora Tab */}
          <Tab eventKey="bitacora" title={<><i className="bi bi-clock-history me-1"></i><span className="d-none d-sm-inline">Bitacora</span></>}>
            <ProjectTimeline
              projectId={projectId}
              project={project}
            />
          </Tab>

          {/* Photos Tab */}
          <Tab eventKey="photos" title={<><i className="bi bi-images me-1"></i><span className="d-none d-sm-inline">Fotos</span></>}>
            {photosLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="mt-2 text-muted small">Cargando fotos...</p>
              </div>
            ) : photos.length > 0 ? (
              <Row className="g-2">
                {photos.map((photo) => (
                  <Col xs={6} sm={4} key={photo.id}>
                    <Card className="dashboard-card overflow-hidden">
                      <img
                        src={photo.photo_url}
                        alt={photo.caption || 'Foto de progreso'}
                        style={{ height: '150px', objectFit: 'cover', width: '100%' }}
                      />
                      <Card.Body className="p-2">
                        {photo.caption && (
                          <small className="d-block text-truncate">{photo.caption}</small>
                        )}
                        <small className="text-muted d-block">
                          {/* progress_photos has no `created_at` column — the
                              timestamp is `uploaded_at`. */}
                          {formatDate(photo.uploaded_at)}
                        </small>
                        {photo.task && (
                          <small className="text-muted d-block text-truncate">
                            <i className="bi bi-link-45deg me-1"></i>
                            {photo.task.task_number}
                          </small>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Card className="dashboard-card">
                <Card.Body className="text-center py-4">
                  <i className="bi bi-images display-4 text-muted mb-2 d-block"></i>
                  <p className="text-muted mb-0">No hay fotos de progreso</p>
                </Card.Body>
              </Card>
            )}
          </Tab>
        </Tabs>
      </div>
    </Container>
  );
};

export default TechnicianProjectDetail;
